import React, { useState } from "react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";
import { AlertOctagon, Clock, X } from "lucide-react";
import { toast } from "sonner";
import { Tasks } from "../lib/api";
import { fmtDateTime } from "../lib/auditFormat";

/** Inline delay marker for any Back Office task (Insurance / Road Tax / etc).
 *  - When the task is *not* delayed → renders an outline "Mark as Delayed" button.
 *  - When delayed → renders an amber banner with the remarks + "Clear Delay" action.
 *  Surfaces immediately on the Sales Detail page so the DSE knows why a step is slow. */
export function DelayMarker({ task, userId, onChange, readOnly = false, testIdPrefix = "delay" }) {
  const [open, setOpen] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  if (!task) return null;

  const isDelayed = !!task.delay_marked;

  const onMark = async () => {
    if (!remarks.trim()) {
      toast.error("Please add a short reason for the delay");
      return;
    }
    setBusy(true);
    try {
      await Tasks.markDelay(task.id, { remarks: remarks.trim(), user_id: userId });
      toast.success("Delay marked · DSE will see this on Sales Detail", { duration: 2500 });
      setOpen(false);
      setRemarks("");
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to mark delay");
    } finally { setBusy(false); }
  };

  const onClear = async () => {
    setBusy(true);
    try {
      await Tasks.clearDelay(task.id);
      toast.success("Delay cleared", { duration: 2000 });
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to clear");
    } finally { setBusy(false); }
  };

  if (isDelayed) {
    return (
      <div
        className="flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
        data-testid={`${testIdPrefix}-banner`}
      >
        <AlertOctagon className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-700" />
        <div className="min-w-0 flex-1">
          <div className="font-semibold">Marked as delayed</div>
          <div className="mt-0.5 text-xs text-amber-800 break-words">
            {task.delay_remarks || "—"}
          </div>
          {task.delay_marked_at && (
            <div className="mt-0.5 text-[10px] uppercase tracking-wider text-amber-600">
              Logged {fmtDateTime(task.delay_marked_at)}
            </div>
          )}
        </div>
        {!readOnly && (
          <Button
            variant="ghost" size="sm" onClick={onClear} disabled={busy}
            className="h-7 gap-1 text-xs text-amber-800 hover:bg-amber-100 hover:text-amber-900"
            data-testid={`${testIdPrefix}-clear-btn`}
          >
            <X className="h-3 w-3" /> Clear
          </Button>
        )}
      </div>
    );
  }

  if (readOnly) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline" size="sm"
          className="gap-1.5 border-amber-300 text-amber-800 hover:bg-amber-50"
          data-testid={`${testIdPrefix}-open-btn`}
        >
          <Clock className="h-3.5 w-3.5" /> Mark as Delayed
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertOctagon className="h-5 w-5 text-amber-600" /> Mark this task as delayed
          </DialogTitle>
          <DialogDescription>
            Add a short reason. This shows up on the Sales Detail page so the DSE
            can keep the customer informed.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">Reason / Remarks *</Label>
          <Textarea
            rows={3} value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="e.g. Waiting on underwriter clearance / RTO system down"
            data-testid={`${testIdPrefix}-remarks`}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={onMark} disabled={busy || !remarks.trim()}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid={`${testIdPrefix}-submit-btn`}>
            {busy ? "Saving…" : "Mark Delayed"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
